import { useEffect, useState } from "react";
import { createContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  deliverySuccessMessage,
  toastErrorMessage,
} from "../../../utils/toastMessage/toastMessage";
import {
  confirmBookSnappTripService,
  createBookSnappTripService,
  getCityHotelsSnappTripService,
  getHotelRoomsSnappTripService,
  getHotelSnappTripService,
  getPassengersUserService,
  searchCitiesAndHotelsSnappTripService,
  updateBookSnappTripService,
} from "../../../services/userServices";
import { convertRoleToPath, getISOStringDate } from "../../../utils";

export const hotelUserContext = createContext({
  hotels: [],
  setHotels: () => {},
  rooms: [],
  setRooms: () => {},
  passengers: [],
  setPassengers: () => {},
  hotel: {},
  setHotel: () => {},
  room: {},
  setRoom: () => {},
  selectedItem: {},
  setSelectedItem: () => {},
  citiesHotels: {},
  setCitiesHotels: () => {},
  filter: "",
  setFilter: () => {},
  from: "",
  setFrom: () => {},
  to: "",
  setTo: () => {},
  showSearchItems: null,
  setShowSearchItems: () => {},
  pageCount: null,
  setPageCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  itemsCount: null,
  setItemsCount: () => {},
  total: null,
  setTotal: () => {},
  handleSearchCitiesAndHotels: () => {},
  handleGetCityHotels: () => {},
  handleGetHotel: () => {},
  handleGetHotelRooms: () => {},
  handleCreateBook: () => {},
  handleUpdateBook: () => {},
  handleConfirmBook: () => {},
  handleGetPassengers: () => {},
});

const HotelUserContext = ({ children }) => {
  const dispatch = useDispatch();

  const auth = useSelector((state) => state.auth);

  const { pathname, state: sRoom } = useLocation();
  const { id } = useParams();

  const [searchParams] = useSearchParams();

  const [hotels, setHotels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [passengers, setPassengers] = useState([]);
  const [hotel, setHotel] = useState({});
  const [room, setRoom] = useState({});
  const [citiesHotels, setCitiesHotels] = useState({});
  const [selectedItem, setSelectedItem] = useState({});
  const [filter, setFilter] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [showSearchItems, setShowSearchItems] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsCount, setItemsCount] = useState(100);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setHotels([]);
    setRooms([]);
    setHotel({});
    setRoom({});
    setCitiesHotels({});
    setSelectedItem({});
    setFilter("");
    setFrom("");
    setTo("");
    setTotal(0);
  }, [pathname]);

  // Search Cities And Hotels
  const handleSearchCitiesAndHotels = async (event) => {
    event?.preventDefault();

    const objData = {
      q: filter,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await searchCitiesAndHotelsSnappTripService(
        objData
      );
      if (status === 200) {
        setCitiesHotels(data.result);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get City Hotels
  const handleGetCityHotels = async (event) => {
    event?.preventDefault();

    const objData = {
      id: selectedItem.id,
      page: pageNumber,
      from: getISOStringDate(from),
      to: getISOStringDate(to),
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getCityHotelsSnappTripService(objData);
      if (status === 200) {
        const result = data.result;

        if (isEmpty(result?.data))
          toastErrorMessage("هتلی در بازه انتخابی یافت نشد");

        setHotels(result?.data);
        setPageCount(Math.ceil(result?.meta?.total / result?.meta?.page_size));
        setTotal(result?.meta?.total);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Hotel
  const handleGetHotel = async (event) => {
    event?.preventDefault();

    const objData = {
      id,
      from: searchParams.get("from"),
      to: searchParams.get("to"),
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getHotelSnappTripService(objData);
      if (status === 200) {
        setHotel(data.result);
        handleGetHotelRooms();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Hotel Rooms
  const handleGetHotelRooms = async (event) => {
    event?.preventDefault();

    const objData = {
      id,
      from: searchParams.get("from"),
      to: searchParams.get("to"),
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getHotelRoomsSnappTripService(objData);
      if (status === 200) {
        setRooms(data.result);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Book
  const handleCreateBook = async (userData, totalAmount) => {
    const objData = {
      ...userData,
      date_from: sRoom.from,
      date_to: sRoom.to,
      hotel_id: sRoom?.hotel?.id,
      totalAmount,
      services: userData?.services?.map((item) => item?.value),
    };

    // Todo: delete this line
    console.log(objData);
    return;

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createBookSnappTripService(objData);
      if (status === 200) {
        deliverySuccessMessage(
          data.resultMessage,
          `/${convertRoleToPath(auth.role)}/hotel-orders`,
          "5000"
        );
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Update Book
  const handleUpdateBook = async (userData) => {
    const objData = {
      reservation_code: sRoom.reservation_code,
      ...userData,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await updateBookSnappTripService(objData);
      if (status === 200) {
        // Todo: complete this line
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Confirm Book
  const handleConfirmBook = async (userData) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await confirmBookSnappTripService(
        room.reservation_code
      );
      if (status === 200) {
        // Todo: complete this line
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Passengers
  const handleGetPassengers = async (filter) => {
    const objData = {
      filter,
    };
    try {
      const { status, data } = await getPassengersUserService(objData);
      if (status === 200) {
        setPassengers(data.result?.passengers);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    }
  };

  return (
    <hotelUserContext.Provider
      value={{
        hotels,
        setHotels,
        rooms,
        setRooms,
        passengers,
        setPassengers,
        hotel,
        setHotel,
        room,
        setRoom,
        citiesHotels,
        setCitiesHotels,
        selectedItem,
        setSelectedItem,
        filter,
        setFilter,
        from,
        setFrom,
        to,
        setTo,
        showSearchItems,
        setShowSearchItems,
        pageCount,
        setPageCount,
        pageNumber,
        setPageNumber,
        itemsCount,
        setItemsCount,
        total,
        setTotal,
        handleSearchCitiesAndHotels,
        handleGetCityHotels,
        handleGetHotel,
        handleGetHotelRooms,
        handleCreateBook,
        handleUpdateBook,
        handleConfirmBook,
        handleGetPassengers,
      }}
    >
      {children}
    </hotelUserContext.Provider>
  );
};

export default HotelUserContext;
